/**
 * Catálogo de colunas da tabela do comprador (ADR-0078).
 *
 * Duas visões da mesma fila: POR REQUISIÇÃO (padrão) e POR ITEM (o formato
 * da planilha que o comprador usava: uma linha por item, com a obra, os
 * fornecedores cotados e a retirada ao lado). Todas as colunas possíveis
 * estão aqui; a pessoa escolhe quais ver e salva como a sua visualização.
 *
 * Coluna: { key, label, valor(linha) -> texto/array p/ filtro e ordem,
 *           bruto?(linha) -> chave de ordenação (datas, números),
 *           largura?, num?, fixa? }.
 * A célula rica (pílula, barra) é decidida na tabela, pela `key`.
 */
import { SITUACAO_ITEM_LABEL, TIPO_TERCEIRO_LABEL } from "../../../services/compras";
import { SITUACAO_LABEL, rotuloPrazo } from "../../../services/requisicoesCompraUi";
import { formatarDataLocal, formatarQuantidade, formatarReaisBR } from "../../../utils/dinheiro";

export const MODO_REQUISICAO = "requisicao";
export const MODO_ITEM = "item";
export const CHAVE_PREFERENCIA = "req_compra_tabela";

const data = (iso) => (iso ? formatarDataLocal(iso) : "");
const dinheiro = (v) => (v === null || v === undefined || v === "" ? "" : formatarReaisBR(v));
const numero = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
const prioridade = (p) => (p === "URGENTE" ? "Urgente" : "Normal");
const TIPO_ITEM_LABEL = { CADASTRO: "Cadastrado", LIVRE: "Não cadastrado", TERCEIRO: "Terceiro" };

export function diasParado(r) {
  if (!r.situacao_alterada_em) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(r.situacao_alterada_em).getTime()) / 86400000));
}

/** "MESTRE= R$ 1.940,00 · IPÊ= NT" — como o comprador escrevia na planilha. */
export function textoDasCotacoes(cotacoes = []) {
  return cotacoes
    .map((c) => `${c.fornecedor_nome}= ${c.nao_tem ? "NT" : (c.total ? formatarReaisBR(c.total) : "aguardando")}`)
    .join(" · ");
}

export const COLUNAS_REQUISICAO = [
  { key: "numero", label: "Nº", largura: 120, fixa: true, valor: (r) => r.numero },
  { key: "criada", label: "Criada em", largura: 110, valor: (r) => data(r.created_at), bruto: (r) => r.created_at },
  { key: "cliente", label: "Cliente / obra", largura: 200, valor: (r) => r.cliente_nome },
  { key: "projeto", label: "Projeto", largura: 200, valor: (r) => r.projeto_resumo },
  { key: "origem", label: "Origem", largura: 140, valor: (r) => r.origem_label },
  { key: "terceiros", label: "Tipo de terceiro", largura: 170, valor: (r) => (r.tipos_terceiro || []).map((t) => TIPO_TERCEIRO_LABEL[t] || t) },
  { key: "solicitantes", label: "Solicitante", largura: 160, valor: (r) => ((r.solicitantes || []).length ? r.solicitantes : [r.solicitado_por_nome].filter(Boolean)) },
  { key: "prioridade", label: "Prioridade", largura: 110, valor: (r) => prioridade(r.prioridade) },
  { key: "status", label: "Status", largura: 150, valor: (r) => SITUACAO_LABEL[r.situacao] || r.situacao },
  { key: "itens", label: "Itens", largura: 150, valor: (r) => `${r.itens_comprados}/${r.itens_total}`, bruto: (r) => r.itens_total },
  { key: "itens_a_cotar", label: "Itens a cotar", largura: 110, num: true, valor: (r) => r.itens_pendentes },
  { key: "itens_em_andamento", label: "Itens em andamento", largura: 130, num: true, valor: (r) => r.itens_em_cotacao },
  { key: "itens_aprovados", label: "Itens aprovados", largura: 120, num: true, valor: (r) => r.itens_aprovados },
  { key: "itens_comprados", label: "Itens comprados", largura: 120, num: true, valor: (r) => r.itens_comprados },
  { key: "prazo", label: "Prazo de cotação", largura: 130, valor: (r) => data(r.prazo_cotacao), bruto: (r) => r.prazo_cotacao },
  { key: "situacao_prazo", label: "Situação do prazo", largura: 140, valor: (r) => rotuloPrazo(r.dias_para_prazo).texto, bruto: (r) => r.dias_para_prazo },
  { key: "data_necessaria", label: "Data necessária", largura: 130, valor: (r) => data(r.data_necessaria), bruto: (r) => r.data_necessaria },
  { key: "previsao_entrega", label: "Previsão de entrega", largura: 140, valor: (r) => data(r.previsao_entrega), bruto: (r) => r.previsao_entrega },
  { key: "comprador", label: "Comprador", largura: 150, valor: (r) => r.comprador_nome },
  { key: "valor_aberto", label: "Valor em OC aberta", largura: 140, num: true, valor: (r) => dinheiro(Number(r.valor_em_aberto) ? r.valor_em_aberto : ""), bruto: (r) => numero(r.valor_em_aberto) },
  { key: "juntou", label: "Juntou", largura: 150, valor: (r) => (r.consolidadas || []).map((c) => c.numero) },
  { key: "parada", label: "Parada há (dias)", largura: 120, num: true, valor: (r) => diasParado(r) },
  { key: "justificativa", label: "Justificativa", largura: 240, filtro: false, valor: (r) => r.justificativa },
];

export const PADRAO_REQUISICAO = ["numero", "cliente", "origem", "itens", "status", "prazo", "comprador"];

export const COLUNAS_ITEM = [
  { key: "data", label: "Data", largura: 100, valor: (i) => data(i.requisicao_criada_em), bruto: (i) => i.requisicao_criada_em },
  { key: "numero", label: "Nº", largura: 120, valor: (i) => i.requisicao_numero },
  { key: "item", label: "Item", largura: 260, fixa: true, valor: (i) => i.nome },
  { key: "quantidade", label: "Quant.", largura: 90, num: true, valor: (i) => formatarQuantidade(i.quantidade), bruto: (i) => numero(i.quantidade) },
  { key: "unidade", label: "Un.", largura: 70, valor: (i) => i.unidade_efetiva },
  { key: "obra", label: "Obra / cliente", largura: 180, valor: (i) => i.cliente_efetivo },
  { key: "projeto", label: "Projeto", largura: 180, valor: (i) => i.projeto_resumo },
  { key: "solicitantes", label: "Solicitante", largura: 150, valor: (i) => i.solicitantes || [] },
  { key: "tipo", label: "Tipo de item", largura: 130, valor: (i) => TIPO_ITEM_LABEL[i.tipo] || i.tipo },
  { key: "terceiro", label: "Tipo de terceiro", largura: 170, valor: (i) => i.tipo_terceiro_label },
  { key: "codigo", label: "Código", largura: 130, valor: (i) => i.produto_codigo },
  { key: "categoria", label: "Categoria", largura: 150, valor: (i) => i.categoria_label },
  { key: "marca", label: "Marca", largura: 120, valor: (i) => i.marca },
  { key: "modelo", label: "Modelo", largura: 120, valor: (i) => i.modelo },
  { key: "especificacao", label: "Especificação", largura: 220, filtro: false, valor: (i) => i.especificacao },
  { key: "observacao", label: "Observação", largura: 200, filtro: false, valor: (i) => i.observacao },
  { key: "origem", label: "Origem", largura: 140, valor: (i) => i.origem_label },
  { key: "veio_de", label: "Veio de (junção)", largura: 140, valor: (i) => i.requisicao_origem_numero },
  { key: "situacao_item", label: "Status do item", largura: 140, valor: (i) => SITUACAO_ITEM_LABEL[i.situacao] || i.situacao },
  { key: "status", label: "Status da requisição", largura: 160, valor: (i) => SITUACAO_LABEL[i.requisicao_situacao] || i.requisicao_situacao },
  { key: "prioridade", label: "Prioridade", largura: 110, valor: (i) => prioridade(i.prioridade) },
  { key: "prazo", label: "Prazo de cotação", largura: 130, valor: (i) => data(i.prazo_cotacao), bruto: (i) => i.prazo_cotacao },
  { key: "data_necessaria", label: "Data necessária", largura: 130, valor: (i) => data(i.data_necessaria), bruto: (i) => i.data_necessaria },
  { key: "cotacoes", label: "Fornecedores cotados", largura: 300, filtro: false, valor: (i) => textoDasCotacoes(i.cotacoes_resumo) },
  { key: "fornecedores", label: "Fornecedor (filtro)", largura: 170, valor: (i) => i.fornecedores_cotados || [] },
  { key: "n_cotacoes", label: "Nº de cotações", largura: 120, num: true, valor: (i) => (i.cotacoes_resumo || []).length },
  { key: "menor_total", label: "Menor preço", largura: 130, num: true, valor: (i) => dinheiro(i.menor_total), bruto: (i) => numero(i.menor_total) },
  { key: "menor_fornecedor", label: "Fornecedor do menor preço", largura: 180, valor: (i) => i.menor_fornecedor },
  { key: "vencedor", label: "Vencedor", largura: 160, valor: (i) => i.vencedor_nome },
  { key: "vencedor_total", label: "Valor do vencedor", largura: 140, num: true, valor: (i) => dinheiro(i.vencedor_total), bruto: (i) => numero(i.vencedor_total) },
  { key: "entrega", label: "Entrega / retirada", largura: 240, filtro: false, valor: (i) => i.entrega_obs },
  { key: "anexos", label: "Anexos", largura: 90, num: true, valor: (i) => i.n_anexos },
  { key: "oc", label: "Ordem de compra", largura: 140, valor: (i) => i.ordem_compra_numero },
  { key: "comprador", label: "Comprador", largura: 150, valor: (i) => i.comprador_nome },
];

export const PADRAO_ITEM = [
  "data", "numero", "item", "quantidade", "obra", "cotacoes", "vencedor", "situacao_item", "entrega",
];

export const CATALOGO = {
  [MODO_REQUISICAO]: { colunas: COLUNAS_REQUISICAO, padrao: PADRAO_REQUISICAO },
  [MODO_ITEM]: { colunas: COLUNAS_ITEM, padrao: PADRAO_ITEM },
};

export const modoValido = (m) => (m === MODO_ITEM ? MODO_ITEM : MODO_REQUISICAO);
